/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        AI In-Editor Actions, Tone Presets & Prompt Templates (Task 10.2.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import {
  ArrowRight,
  Minimize2,
  Maximize2,
  Smile,
  ShieldCheck,
  Send,
  type LucideIcon,
} from 'lucide-react';

export type AiActionType =
  | 'continue'
  | 'summarize'
  | 'expand'
  | 'change-tone'
  | 'grammar-fix'
  | 'custom-prompt';

export interface AiTonePreset {
  id: string;
  name: string;
  description: string;
  promptInstruction: string;
}

export const AI_TONE_PRESETS: AiTonePreset[] = [
  {
    id: 'professional',
    name: 'Professional',
    description: 'Formal, objective and trustworthy',
    promptInstruction: 'rewrite with a professional, scholarly, and formal tone suitable for B2B enterprise environments',
  },
  {
    id: 'persuasive',
    name: 'Persuasive',
    description: 'Compelling, conversion-focused and inspiring action',
    promptInstruction: 'rewrite with a sharp, highly persuasive tone that drives decisive Call-to-Action',
  },
  {
    id: 'humorous',
    name: 'Humorous',
    description: 'Witty, playful and engaging',
    promptInstruction: 'rewrite with a witty, clever, and entertaining voice while preserving the core message',
  },
  {
    id: 'casual',
    name: 'Casual',
    description: 'Friendly, modern and conversational',
    promptInstruction: 'rewrite in a fresh, friendly, and conversational tone like speaking to a peer',
  },
  {
    id: 'academic',
    name: 'Academic',
    description: 'Rigorous, analytical with deep domain context',
    promptInstruction: 'rewrite with an academic research style, rigorous argumentation, and formal sentence structure',
  },
];

export interface AiActionItem {
  id: AiActionType;
  label: string;
  icon: LucideIcon;
  description: string;
  isReplacement: boolean;
}

export const AI_ACTIONS: AiActionItem[] = [
  {
    id: 'continue',
    label: 'Continue',
    icon: ArrowRight,
    description: 'Naturally extend the idea based on surrounding context',
    isReplacement: false,
  },
  {
    id: 'summarize',
    label: 'Summarize',
    icon: Minimize2,
    description: 'Condense long paragraph into concise bullet points',
    isReplacement: true,
  },
  {
    id: 'expand',
    label: 'Expand',
    icon: Maximize2,
    description: 'Add in-depth details, examples and explanations',
    isReplacement: true,
  },
  {
    id: 'change-tone',
    label: 'Change Tone',
    icon: Smile,
    description: 'Adapt tone to audience (Professional, Persuasive, Humorous...)',
    isReplacement: true,
  },
  {
    id: 'grammar-fix',
    label: 'Fix Grammar',
    icon: ShieldCheck,
    description: 'Fix grammar, punctuation and spelling mistakes',
    isReplacement: true,
  },
  {
    id: 'custom-prompt',
    label: 'Custom Prompt',
    icon: Send,
    description: 'Execute custom instruction on selected text',
    isReplacement: true,
  },
];

export interface BuiltPromptResult {
  systemPrompt: string;
  userMessage: string;
  isReplacement: boolean;
  estimatedCredits: number;
}

export function buildAiPrompt(
  actionId: AiActionType,
  selectedText: string,
  toneId?: string,
  customPrompt?: string,
): BuiltPromptResult {
  const baseSystemPrompt =
    'You are a senior professional AI editor and writing assistant for the Sophia Platform. Return only the processed direct output text without pleasantries, unnecessary commentary, or markdown wrapper blocks unless explicitly requested.';

  const cleanText = selectedText.trim();

  switch (actionId) {
    case 'continue':
      return {
        systemPrompt: baseSystemPrompt,
        userMessage: `Please write 1-3 natural, seamless continuation sentences for the following text:\n\n"${cleanText}"`,
        isReplacement: false,
        estimatedCredits: 5,
      };

    case 'summarize':
      return {
        systemPrompt: baseSystemPrompt,
        userMessage: `Please summarize the following text into a concise paragraph highlighting the core takeaways:\n\n"${cleanText}"`,
        isReplacement: true,
        estimatedCredits: 4,
      };

    case 'expand':
      return {
        systemPrompt: baseSystemPrompt,
        userMessage: `Please elaborate and expand in-depth on the following text with supporting arguments:\n\n"${cleanText}"`,
        isReplacement: true,
        estimatedCredits: 6,
      };

    case 'change-tone': {
      const tone = AI_TONE_PRESETS.find((t) => t.id === toneId) || AI_TONE_PRESETS[0];
      return {
        systemPrompt: baseSystemPrompt,
        userMessage: `Please ${tone.promptInstruction} for the following text:\n\n"${cleanText}"`,
        isReplacement: true,
        estimatedCredits: 5,
      };
    }

    case 'grammar-fix':
      return {
        systemPrompt: baseSystemPrompt,
        userMessage: `Please proofread and correct all spelling, punctuation, and grammatical mistakes in the following text, preserving its original tone and intent:\n\n"${cleanText}"`,
        isReplacement: true,
        estimatedCredits: 3,
      };

    case 'custom-prompt':
      return {
        systemPrompt: baseSystemPrompt,
        userMessage: `${customPrompt?.trim() || 'Improve the following text'}:\n\n"${cleanText}"`,
        isReplacement: true,
        estimatedCredits: 5,
      };

    default:
      return {
        systemPrompt: baseSystemPrompt,
        userMessage: cleanText,
        isReplacement: true,
        estimatedCredits: 5,
      };
  }
}
